import { useEffect, useRef, useState } from "react";

interface PollingOptions {
  /** Poll interval in ms. */
  intervalMs: number;
  /** Pause polling when the tab is hidden. Defaults to true. */
  pauseWhenHidden?: boolean;
  /** Run immediately on mount. Defaults to true. */
  immediate?: boolean;
}

export interface PollingResult<T> {
  data: T | undefined;
  error: string | null;
  /** HTTP status of the last failure, when the error carried one. */
  errorStatus: number | null;
  /** Consecutive failed polls. 0 once a poll succeeds. */
  failureCount: number;
  lastUpdatedAt: number | null;
  loading: boolean;
  refetch: () => Promise<void>;
}

/** Backoff grows from this, not from the poll interval. */
const BACKOFF_BASE_MS = 2000;
/** Never wait longer than this after a failure, whatever the poll interval. */
const BACKOFF_CEILING_MS = 30000;
const MAX_BACKOFF_STEPS = 5;

/**
 * Delay before the next poll.
 *
 * A failure must never push the next attempt *further out* than the normal
 * cadence — that is the opposite of what a recovering dashboard needs. The
 * first version multiplied the interval itself, so five early failures on the
 * 60s vehicle roster scheduled the retry 32 minutes out and the counters sat
 * at "—" for half an hour with nothing on screen to explain it.
 */
function nextDelayMs(intervalMs: number, failures: number): number {
  if (failures <= 0) return intervalMs;
  const backoff = Math.min(
    BACKOFF_BASE_MS * Math.pow(2, failures - 1),
    BACKOFF_CEILING_MS
  );
  return Math.min(backoff, intervalMs);
}

/**
 * Generic polling hook. Pauses on tab hidden, re-fires on visibility return.
 * Built to be swappable with an SSE/WS implementation later — components
 * should only depend on `{ data, error, lastUpdatedAt }`.
 */
export function usePollingQuery<T>(
  fetcher: () => Promise<T>,
  deps: ReadonlyArray<unknown>,
  options: PollingOptions
): PollingResult<T> {
  const { intervalMs, pauseWhenHidden = true, immediate = true } = options;
  const [data, setData] = useState<T | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [failureCount, setFailureCount] = useState(0);
  // Read by the interval callback, which must not be re-created on every
  // failure (that would restart the timer and defeat the backoff).
  const failureRef = useRef(0);
  const cancelledRef = useRef(false);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const run = useRef(async () => {
    setLoading(true);
    try {
      const v = await fetcherRef.current();
      if (cancelledRef.current) return;
      setData(v);
      setLastUpdatedAt(Date.now());
      setError(null);
      setErrorStatus(null);
      failureRef.current = 0;
      setFailureCount(0);
    } catch (err) {
      if (cancelledRef.current) return;
      setError(err instanceof Error ? err.message : "Request failed");
      const status =
        err && typeof err === "object" && "status" in err
          ? Number((err as { status: unknown }).status)
          : NaN;
      setErrorStatus(Number.isFinite(status) ? status : null);
      // A 401 is not congestion: the api client refreshes the token and the
      // next poll normally succeeds, so backing off here only prolongs a blank
      // screen. Observed holding the vehicle roster empty for ~a minute after
      // one transient 401. Back off for throttling and server faults only.
      const shouldBackOff = !Number.isFinite(status) || status !== 401;
      if (shouldBackOff) {
        failureRef.current = Math.min(failureRef.current + 1, MAX_BACKOFF_STEPS);
        setFailureCount(failureRef.current);
      }
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  });

  useEffect(() => {
    cancelledRef.current = false;
    let id: number | undefined;

    // Self-rescheduling timeout rather than a fixed interval, so a failed
    // poll can retry on its own schedule. Retries are spaced out to avoid
    // hammering a rate-limited gateway, but never beyond the normal cadence.
    const schedule = () => {
      const delay = nextDelayMs(intervalMs, failureRef.current);
      id = window.setTimeout(() => {
        void run.current().finally(() => {
          if (!cancelledRef.current && id != null) schedule();
        });
      }, delay);
    };
    const start = () => {
      if (id != null) return;
      if (immediate) void run.current();
      schedule();
    };
    const stop = () => {
      if (id == null) return;
      window.clearTimeout(id);
      id = undefined;
    };

    start();

    const onVis = () => {
      if (!pauseWhenHidden) return;
      if (document.hidden) stop();
      else { void run.current(); start(); }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelledRef.current = true;
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, error, errorStatus, failureCount, lastUpdatedAt, loading, refetch: () => run.current() };
}
